import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Users, Plus, ArrowLeft, Bot, GraduationCap, Lock } from "lucide-react";

const SCOPE_LABEL = { community: "Comunidad completa", franchise: "Franquicia", my_students: "Mis alumnos" };

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(null);
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", question: "", scope: "community" });
  const [closing, setClosing] = useState(false);
  const [teacherOpinion, setTeacherOpinion] = useState("");
  const [busyClose, setBusyClose] = useState(false);
  const canCreate = ["PROFESOR", "FRANQUICIA", "SUPER_ADMIN"].includes(user.role);

  const load = () => api.get("/groups").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const openGroup = async (g) => {
    setOpen(g); setClosing(false);
    const r = await api.get(`/groups/${g.id}/posts`);
    setPosts(r.data);
  };

  const create = async () => {
    try {
      await api.post("/groups", form);
      toast.success("Grupo de discusión creado");
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendPost = async () => {
    if (!post.trim()) return;
    try {
      const r = await api.post(`/groups/${open.id}/posts`, { content: post });
      setPosts([...posts, r.data]); setPost("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const closeGroup = async () => {
    setBusyClose(true);
    try {
      const r = await api.post(`/groups/${open.id}/close`, { teacher_opinion: teacherOpinion });
      setOpen({ ...open, status: "closed", summary: r.data.summary });
      toast.success("Debate cerrado y resumido por Dakota");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyClose(false); }
  };

  if (open) {
    const isOwner = open.teacher_id === user.id || user.role === "SUPER_ADMIN";
    return (
      <div className="fade-up max-w-3xl" data-testid="group-detail">
        <button data-testid="group-back-btn" onClick={() => { setOpen(null); load(); }} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-orange-700 mb-4">
          <ArrowLeft size={15} /> Volver a comunidad
        </button>
        <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2.5 py-1">{SCOPE_LABEL[open.scope]}</span>
        <h1 className="text-2xl font-semibold tracking-tight mt-3">{open.title}</h1>
        <p className="text-neutral-600 mt-2 italic">"{open.question}"</p>
        <p className="text-xs text-neutral-400 mt-1">Moderado por {open.teacher_name} · {open.status === "open" ? "Abierto" : "Cerrado"}</p>

        <div className="mt-6 space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-4" data-testid="group-post">
              <p className="text-xs font-semibold text-orange-700">{p.user_name} <span className="text-neutral-400 font-normal">· {p.role === "PROFESOR" ? "Profesor" : "Alumno"} · {fmtDate(p.date)}</span></p>
              <p className="text-sm mt-1">{p.content}</p>
            </div>
          ))}
          {posts.length === 0 && <p className="text-sm text-neutral-500">Sé el primero en participar.</p>}
        </div>

        {open.status === "open" ? (
          <div className="mt-6 flex gap-2">
            <Input data-testid="group-post-input" value={post} onChange={(e) => setPost(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && sendPost()} placeholder="Escribe tu aportación…" />
            <Button data-testid="group-post-btn" onClick={sendPost} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">Participar</Button>
          </div>
        ) : open.summary && (
          <div className="mt-8 space-y-4">
            <div className="rounded-xl p-6 dakota-glow bg-orange-50 border border-orange-100" data-testid="group-dakota-summary">
              <p className="flex items-center gap-2 text-sm font-semibold text-orange-700 uppercase tracking-wider mb-3"><Bot size={16} /> Resumen y Opinión de Dakota</p>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{open.summary.dakota}</p>
            </div>
            <div className="rounded-xl p-6 bg-white border border-neutral-200" data-testid="group-teacher-opinion">
              <p className="flex items-center gap-2 text-sm font-semibold text-neutral-700 uppercase tracking-wider mb-3"><GraduationCap size={16} /> Opinión del Profesor</p>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{open.summary.teacher_opinion || "—"}</p>
            </div>
          </div>
        )}

        {open.status === "open" && isOwner && (
          <div className="mt-8 bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
            {!closing ? (
              <Button data-testid="group-close-btn" onClick={() => setClosing(true)} variant="outline" className="rounded-full border-orange-300 text-orange-700">
                <Lock size={14} className="mr-1.5" /> Cerrar y resumir con Dakota
              </Button>
            ) : (
              <div className="space-y-3">
                <Textarea data-testid="group-opinion-input" placeholder="Tu opinión como profesor (aparecerá separada de la de Dakota)" rows={3}
                          value={teacherOpinion} onChange={(e) => setTeacherOpinion(e.target.value)} />
                <Button data-testid="group-close-confirm-btn" onClick={closeGroup} disabled={busyClose}
                        className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
                  {busyClose ? "Dakota está resumiendo…" : "Confirmar cierre"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fade-up" data-testid="groups-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Comunidad</h1>
          <p className="text-neutral-500 mt-1">Grupos de discusión moderados por el profesorado.</p>
        </div>
        {canCreate && (
          <Button data-testid="group-add-btn" onClick={() => setShowForm(!showForm)} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
            <Plus size={15} className="mr-1.5" /> Crear grupo
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-6 space-y-3" data-testid="group-form">
          <Input data-testid="group-title-input" placeholder="Título del debate" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea data-testid="group-question-input" placeholder='Pregunta, ej: "¿Creéis que las señales horizontales siguen siendo igual de importantes que las verticales?"'
                    rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
          <div className="flex gap-3">
            <select data-testid="group-scope-select" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                    className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="community">Comunidad completa</option>
              <option value="franchise">Franquicia</option>
              <option value="my_students">Mis alumnos</option>
            </select>
            <Button data-testid="group-create-btn" onClick={create} className="rounded-full bg-neutral-900 text-white">Crear</Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {groups.map((g) => (
          <button key={g.id} onClick={() => openGroup(g)} data-testid="group-card"
                  className="text-left bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-transform">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2.5 py-0.5">{SCOPE_LABEL[g.scope]}</span>
              <span className={`text-xs font-medium ${g.status === "open" ? "text-green-600" : "text-neutral-400"}`}>{g.status === "open" ? "● Abierto" : "Cerrado"}</span>
            </div>
            <h3 className="font-semibold text-lg mt-3">{g.title}</h3>
            <p className="text-sm text-neutral-500 mt-1 line-clamp-2 italic">"{g.question}"</p>
            <p className="text-xs text-neutral-400 mt-3">Por {g.teacher_name} · {fmtDate(g.created_at)}</p>
          </button>
        ))}
        {groups.length === 0 && <p className="text-sm text-neutral-500">Todavía no hay grupos de discusión.</p>}
      </div>
    </div>
  );
}
